// Merkan · Bewegung beim Anzeigen und Scrollen: Blöcke einer Seite blenden ruhig ein (leicht gestaffelt),
// weiter unten liegende erst, wenn sie ins Bild scrollen. Nur opacity und transform, danach ohne Rest-Transform,
// damit feste Elemente (Panels, Dialoge) nicht beeinflusst werden. prefers-reduced-motion schaltet alles ab.
import { useEffect, type RefObject } from 'react';

const ZIELE = [
  '.seitenkopf', '.block', '.kachel', '.tabelle-huelle', '.ue-suche', '.hinweis', '.reiter', '.vg-kopf > *', '.rw-kacheln > *',
  '.st-raster > *', '.aw-raster > *', '.ue-raster > *', '.leer',
].join(', ');
const AUSSCHLUSS = '.panel, .dialog, .schleier, .as-panel, .hub-board-detail, [data-ohne-bewegung]';

export function useErscheinen(ref: RefObject<HTMLElement>, schluessel: string) {
  useEffect(() => {
    const wurzel = ref.current;
    if (!wurzel || typeof IntersectionObserver === 'undefined') return;
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;

    let welle = 0; // Staffelung für Elemente, die im selben Moment erscheinen
    let welleZeit = 0;
    const fertig = (el: HTMLElement) => {
      el.classList.remove('erscheint', 'erscheint-sichtbar');
      el.style.removeProperty('--erscheinen-verzug');
      el.dataset.erschienen = '1';
    };
    const io = new IntersectionObserver((eintraege) => {
      for (const e of eintraege) {
        if (!e.isIntersecting) continue;
        const el = e.target as HTMLElement;
        io.unobserve(el);
        const jetzt = performance.now();
        if (jetzt - welleZeit > 120) welle = 0;
        welleZeit = jetzt;
        el.style.setProperty('--erscheinen-verzug', `${Math.min(welle, 6) * 45}ms`);
        welle += 1;
        requestAnimationFrame(() => el.classList.add('erscheint-sichtbar'));
        const ende = (ev: TransitionEvent) => { if (ev.target === el) { el.removeEventListener('transitionend', ende); fertig(el); } };
        el.addEventListener('transitionend', ende);
        window.setTimeout(() => { el.removeEventListener('transitionend', ende); fertig(el); }, 1100);
      }
    }, { rootMargin: '0px', threshold: 0 });

    const vorbereiten = (el: Element) => {
      if (!(el instanceof HTMLElement) || el.dataset.erschienen || el.classList.contains('erscheint')) return;
      if (el.closest(AUSSCHLUSS)) return;
      // Verschachtelte Ziele nicht doppelt bewegen
      const eltern = el.parentElement?.closest('.erscheint, [data-erschienen]');
      if (eltern && wurzel.contains(eltern) && eltern.classList.contains('erscheint')) { el.dataset.erschienen = '1'; return; }
      el.classList.add('erscheint');
      io.observe(el);
    };
    const durchsuchen = (n: ParentNode) => n.querySelectorAll(ZIELE).forEach(vorbereiten);
    durchsuchen(wurzel);
    const mo = new MutationObserver((liste) => {
      for (const m of liste) m.addedNodes.forEach((k) => {
        if (!(k instanceof HTMLElement)) return;
        if (k.matches(ZIELE)) vorbereiten(k);
        durchsuchen(k);
      });
    });
    mo.observe(wurzel, { childList: true, subtree: true });
    // Sicherheitsnetz: nichts bleibt unsichtbar, falls ein Beobachter nicht auslöst (z. B. Element ganz am Seitenende)
    const pruefen = () => wurzel.querySelectorAll('.erscheint:not(.erscheint-sichtbar)').forEach((el) => {
      if ((el as HTMLElement).getBoundingClientRect().top < window.innerHeight) { io.unobserve(el); el.classList.add('erscheint-sichtbar'); window.setTimeout(() => fertig(el as HTMLElement), 600); }
    });
    const takt = window.setInterval(pruefen, 700);
    return () => {
      mo.disconnect();
      io.disconnect();
      window.clearInterval(takt);
      wurzel.querySelectorAll('.erscheint').forEach((el) => fertig(el as HTMLElement));
    };
  }, [ref, schluessel]);
}
